import { useQuery } from '@tanstack/react-query';
import { apiFetch } from './client';

// Separate module rather than folded into companions.ts (read first, per the task-14 brief):
// companions.ts is entirely per-companion (list/create/setActive/allocate/respec, all keyed off a
// specific companion id or the flat roster). Slot state (pets/SlotStateDto.java,
// pets/PetSlotService.java) is an ACCOUNT-level capacity concept — how many companions the account
// may own, not any one companion's own data — with its own static route (GET /companions/slots,
// CompanionController) and its own natural cache key that would otherwise collide in shape (both
// would want an `all`-style key) without actually sharing invalidation triggers. Kept as its own
// module for the same reason wallet.ts stays separate from bag.ts.

// Server: pets/SlotStateDto.java — GET /api/v1/companions/slots. `nextUnlock` is null once the
// account has reached the hard cap of 6 (PetSlotService.MAX_SLOTS); otherwise it names the single
// next thing that would raise `unlocked` — kind "level" (detail 10 or 20, a still-locked free
// level threshold) or kind "purchase" (detail 4/5/6, the next purchasable slot's ordinal).
// Deliberately carries no price — that's catalog content (slot_4/slot_5/slot_6 skus), not a
// server-side constant.
export type NextSlotUnlockDto = { kind: 'level' | 'purchase'; detail: number };

export type SlotStateDto = {
  unlocked: number;
  used: number;
  nextUnlock: NextSlotUnlockDto | null;
};

export const petSlotsKeys = {
  all: ['petSlots'] as const,
};

export const getPetSlots = () => apiFetch<SlotStateDto>('/companions/slots');

export function usePetSlots() {
  return useQuery({ queryKey: petSlotsKeys.all, queryFn: getPetSlots });
}

// No mutation in this module — a slot purchase is a catalog purchase (catalog.ts's
// useCatalogPurchase, sku slot_4/slot_5/slot_6, PetSlotReward-driven grant) and a companion
// creation is companions.ts's useCreateCompanion; both change what GET /companions/slots would
// return next (`used`/`unlocked`). Task 15 (the SlotsRow screen work this comment used to flag as
// a follow-up) wired invalidateQueries({queryKey: petSlotsKeys.all}) into both of those existing
// mutations' own onSuccess — see their own header comments in companions.ts/catalog.ts.
